/**
 * CR-7747: View and Download for an Approval Register certificate.
 *
 * Both the Quality > Approval Register popup and the Suppliers > Approvals
 * tab used to link straight to the file's storage URL. Wherever /media/ is
 * not served that is a 404 — "View Certificate" opened a Not Found page and
 * Download saved "Couldn't download - No file" — and a plain link cannot
 * carry the auth token anyway. The file is now fetched through the
 * authenticated API and handed to the browser as a blob.
 */
import { useCallback, useState } from 'react';

import { qualityAPI } from '../../lib/api';
import { blobErrorMessage, dispositionFilename, saveBlob } from '../../utils/fileDownload';

import { toast } from 'sonner';

export type CertificateAction = 'view' | 'download';

/**
 * Types a browser tab renders without running anything the uploader wrote.
 * Mirrors `_INLINE_SAFE_EXTENSIONS` in quality/views/approval_certificate.py,
 * which sends everything else (Word, Excel) as application/octet-stream.
 */
const VIEWABLE_TYPES = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
]);

/** How long a viewed certificate's blob URL stays valid — long enough for the tab to load it. */
const VIEW_URL_LIFETIME_MS = 60_000;

/**
 * Open a blank tab for View while still inside the click. A tab opened after
 * the network round trip counts as a popup, and Chrome may block it.
 */
function openPlaceholderTab(): Window | null {
  const tab = window.open('', '_blank');
  if (!tab) return null;
  try {
    tab.opener = null;
    tab.document.title = 'Loading certificate…';
    tab.document.body.textContent = 'Loading certificate…';
  } catch {
    // Some browsers hand back a tab we cannot write to; it still navigates.
  }
  return tab;
}

/**
 * Fetch an approval's certificate and show it in a new tab or save it.
 *
 * View falls back to saving the file when the browser cannot render it
 * (Word / Excel) or blocked the tab, and says so, so the user always gets
 * the file and knows where it went.
 *
 * @param id - The supplier-accreditation (approval) id
 * @param filename - The record's file name, used only if the response offers none
 * @param action - 'view' to open it in a new tab, 'download' to save it
 * @returns Resolves once the file is shown or saved, or the error toasted
 */
export async function openAccreditationCertificate(
  id: number,
  filename: string,
  action: CertificateAction,
): Promise<void> {
  const tab = action === 'view' ? openPlaceholderTab() : null;
  try {
    const res = await qualityAPI.downloadSupplierAccreditationCertificate(id);
    const header = res.headers['content-type'];
    const type = (typeof header === 'string' ? header : '').split(';')[0].trim().toLowerCase();

    if (tab && VIEWABLE_TYPES.has(type)) {
      const url = window.URL.createObjectURL(new Blob([res.data], { type }));
      tab.location.href = url;
      setTimeout(() => window.URL.revokeObjectURL(url), VIEW_URL_LIFETIME_MS);
      return;
    }
    tab?.close();
    // The server vets the name it offers; the record's name is writable.
    const name = dispositionFilename(res.headers['content-disposition']) ?? filename;
    saveBlob(res.data, name, type || 'application/octet-stream');
    if (action === 'view') {
      toast.info(
        tab
          ? "This file type can't be shown in the browser, so it has been downloaded."
          : 'Your browser blocked the new tab, so the certificate has been downloaded instead.',
      );
    }
  } catch (err: unknown) {
    tab?.close();
    // A 404 is an expected outcome (nothing attached, or the file has gone
    // from storage) and the toast carries the server's reason.
    const status = (err as { response?: { status?: number } }).response?.status;
    if (status !== 404) console.error('Failed to load accreditation certificate:', err);
    toast.error(await blobErrorMessage(err, 'The certificate could not be loaded.'));
  }
}

interface AccreditationCertificateState {
  /** The action in flight, or null when idle. */
  pending: CertificateAction | null;
  /** View or download one approval's certificate; ignored while another is in flight. */
  run: (id: number, filename: string, action: CertificateAction) => Promise<void>;
}

/**
 * {@link openAccreditationCertificate} with an in-flight flag, so the buttons
 * can show a spinner and a double click does not fetch the file twice.
 *
 * @example
 * const { pending, run } = useAccreditationCertificate();
 * <Button disabled={pending !== null} onClick={() => run(a.id, a.certificate_file_name, 'view')}>
 *   View Certificate
 * </Button>
 *
 * @returns The in-flight action and a `run` function
 */
export function useAccreditationCertificate(): AccreditationCertificateState {
  const [pending, setPending] = useState<CertificateAction | null>(null);

  const run = useCallback(
    async (id: number, filename: string, action: CertificateAction) => {
      if (pending) return;
      setPending(action);
      try {
        await openAccreditationCertificate(id, filename || 'certificate', action);
      } finally {
        setPending(null);
      }
    },
    [pending],
  );

  return { pending, run };
}
